import type {Category} from '~/types/category'
import { FALLBACK_CATEGORIES } from '~/constants/categories';


export const useCategories = () => {

  const api = useApi();
  const { locale } = useI18n();
  const categories = useState<Category[]>('navbar-categories', () => [])
  const isLoading = useState<boolean>('navbar-loading', () => false)
  const error = useState<string | null>('navbar-error', () => null)

  // Returns the list instead of assigning it, so the same loader can back both
  // the per-request state and useAsyncData's cache.
  const loadCategories = async (): Promise<Category[]> => {
    try {
      const raw = await api<Category[]>('/Public/CategoryProduct')
      return raw.toSorted((a, b) => a.ord - b.ord);
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      console.error('Failed to fetch navbar categories:', e)
      return FALLBACK_CATEGORIES;
    }
  }

  const fetchCategories = async () => {
    isLoading.value = true
    error.value = null

    try {
      categories.value = await loadCategories()
    } finally {
      isLoading.value = false
    }
  }

  // Called once from app.vue.
  //
  // Prerendering renders every route in a single process, and useAsyncData is
  // deduplicated by key across those renders: the handler runs once, but its
  // `data` is replayed into every route's payload. `useState` is per-render.
  // So the fetch must NOT populate the state as a side effect — only the one
  // route that happened to run the handler would see it, and every other
  // prerendered page would ship an empty navbar. Seed the state from `data`,
  // which is present on every render.
  //
  // The key carries the locale so bg and en pages don't share a single fetch.
  const initCategories = async () => {
    const { data } = await useAsyncData(
      `navbar-categories:${locale.value}`,
      loadCategories,
      { default: (): Category[] => [] },
    )
    categories.value = data.value ?? []
  }

  return {
    categories: readonly(categories),
    isLoading: readonly(isLoading),
    error: readonly(error),
    fetchCategories,
    initCategories
  }
}
